import styled from "@emotion/styled";
import { APP_CONTENT_WIDTH } from "src/constants/layout";

export const LeapChatContentPositioner = styled.div`
  label: LeapChatContentPositioner;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  position: absolute;
  padding-bottom: var(--app-bottom, 0px);
  inset: 0;
  --leap-chat-width: min(100%, ${APP_CONTENT_WIDTH}px);
  align-items: center;
`;

export const LeapChatContainer = styled(LeapChatContentPositioner)`
  label: LeapChatContainer;
  &::after {
    content: "";
    display: block;
    height: 4rem;
    background: linear-gradient(
      to top,
      var(--color-cream, #fffcf3),
      transparent
    );
    position: absolute;
    bottom: var(--app-bottom, 0px);
    left: 0;
    width: 100%;
    z-index: 1;
    pointer-events: none;
  }
`;

export const LeapChatViewport = styled.div`
  label: MessageListViewport;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 1rem;
  padding-top: var(--app-top, 0px);
  width: 100%;
  min-width: 100%;
`;

export const MessageListContent = styled.div`
  label: MessageListContent;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  min-height: 100%;
  padding: 1.6rem 0 0;
  width: var(--leap-chat-width);
  min-width: var(--leap-chat-width);
`;

export const AssistantMessageRoot = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.6rem 0;
`;

export const AssistantAvatar = styled.div`
  flex-shrink: 0;
  width: 3.2em;
  height: 3.2em;
  min-width: 3.2em;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--color-cream, #fffcf3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const AssistantMessageContent = styled.div`
  background: var(--color-white);
  box-shadow: var(--light-shadow);
  border-radius: 8px;
  padding: 1em 1em 0.8em;
  font-weight: 400;
  font-size: var(--font-size, 1rem);
  line-height: 1.4;
  letter-spacing: -0.02em;
  min-width: 0;
`;

export const AssistantMessageBody = styled.div`
  min-width: 0;
  flex: 1;
`;

export const UserMessageRoot = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0.6rem 0;
`;

export const UserMessageContent = styled.div`
  background-color: var(--color-midmorning-blue);
  border-radius: 8px;
  padding: 1em 1em 0.8em;
  font-weight: 400;
  font-size: var(--font-size, 1rem);
  line-height: 1.4;
  letter-spacing: -0.02em;
  max-width: 85%;
`;

export const MarkdownWrapper = styled.div`
  p {
    margin-top: 1rem;
    margin-bottom: 1em;
    line-height: 1.4;
  }

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.3;
    margin-top: 1.2em;
    margin-bottom: 0.4em;
    font-weight: 600;

    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: 1.35em;
  }

  h2 {
    font-size: 1.2em;
  }

  h3 {
    font-size: 1.08em;
  }

  h4 {
    font-size: 1em;
  }

  h5 {
    font-size: 0.92em;
    font-weight: 500;
  }

  h6 {
    font-size: 0.85em;
    font-weight: 500;
    color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
  }

  ul,
  ol {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
    padding-left: 1.5em;

    li {
      padding: 0;
      margin: 0;

      &:before {
        content: none;
      }
    }
  }
`;

export const MessageFooter = styled.div`
  margin-top: 0.8em;
  font-size: 0.9em;
  color: var(--color-charcoal-60);
  font-weight: 400;
  letter-spacing: -0.02em;
`;

export const FooterName = styled.span`
  font-weight: 500;
  color: var(--color-charcoal-80);
`;

// Hero input (NewConversationView)
export const HeroContainer = styled.div`
  --chat-input-min-height: 5.5em;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.2rem;
  gap: 1rem;
  width: var(--leap-chat-width);
`;

export const HeroHeading = styled.h1`
  font-family: var(--font-family-serif);
  font-size: 1.4rem;
  font-weight: 300;
  color: var(--color-charcoal);
  text-align: center;
  line-height: 1.1;
  letter-spacing: -0.015em;
  margin: 0;
  text-wrap: balance;
`;

export const HeroInputCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--color-white);
  border: 1px solid rgba(33, 33, 33, 0.12);
  border-radius: 8px;
  box-shadow: var(--light-shadow);
  overflow: hidden;
  transition:
    box-shadow 0.2s ease-out,
    border-color 0.2s ease-out;

  &:focus-within {
    border: 1px solid var(--color-sunrise-blue);
    box-shadow:
      inset 0 0 0 1px var(--color-sunrise-blue),
      var(--light-shadow);
  }
`;

export const HeroInput = styled.textarea`
  width: 100%;
  min-height: var(--chat-input-min-height, 2.4em);
  max-height: 14em;
  border: none;
  padding: 1rem 1.2rem 0.5rem;
  font-size: 1.05rem;
  font-family: inherit;
  background: transparent;
  resize: none;
  outline: none;
  word-break: break-word;
  overflow-y: auto;
  color: var(--color-charcoal);
  letter-spacing: -0.02em;

  &::placeholder {
    color: rgba(33, 33, 33, 0.4);
  }
`;

export const HeroToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem 0.75rem;
`;

export const ComposerCardArea = styled.div`
  label: ComposerCardArea;
  z-index: 5;
  padding: 0 0.2rem 0.5rem;
  pointer-events: none;
  position: sticky;
  bottom: 0;
  width: var(--leap-chat-width);
  margin: 0 auto;
  margin-top: 1rem;

  > * {
    pointer-events: auto;
    margin: 0 auto;
  }
`;

// Archive view
export const ArchiveList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: var(--leap-chat-width);
  min-width: var(--leap-chat-width);
`;

export const ArchiveItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 12px 16px;
  background: var(--color-white);
  border: 1px solid rgba(33, 33, 33, 0.1);
  border-radius: 8px;
  box-shadow: var(--light-shadow);
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.15s ease-out;
  margin-bottom: 0.5rem;

  &:hover {
    box-shadow: var(--large-shadow);
  }

  .archive-item-title {
    font-size: 0.95rem;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--color-charcoal);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .archive-item-date {
    font-size: 0.8rem;
    letter-spacing: -0.02em;
    color: rgba(33, 33, 33, 0.5);
  }

  .archive-item-summary {
    font-size: 0.85rem;
    color: var(--color-charcoal-60);
    letter-spacing: -0.02em;
    line-height: 1.4;
    margin: 2px 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

export const ArchiveHeader = styled.div`
  padding: 1rem 0 0.5rem;
  font-family: var(--font-family-serif);
  font-size: 1.1rem;
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.015em;
  color: var(--color-charcoal);
  margin: 0.5rem 0 0.6rem;
`;

// Floating icon buttons
export const FloatingIconButtonPosition = styled.div`
  position: absolute;
  top: calc(0.75rem + var(--app-top, 0px));
  z-index: 10;

  svg {
    width: 1.1rem;
    height: auto;
  }
`;

export const NavPosLeft = styled(FloatingIconButtonPosition)`
  left: 0.75rem;
`;

export const NavPosRight = styled(FloatingIconButtonPosition)`
  right: 0.75rem;
`;
